import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import StudyPackCard from "../../../components/portal/StudyPackCard";
import { getScheduleFilesByClassIdAndDateAPI } from "../../../services/portal/student/studentService";
import { formatDate } from "../../../utility/Common";
import {
  getMyPaidClassesAPI,
  getPayedInformationAPI,
} from "../../../services/portal/student/studentService";
import PaymentBanner from "../../../components/portal/PaymentBanner";

const StudyPacksPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId = searchParams.get("classId");
  const scheduleId = searchParams.get("scheduleId");
  const [classDetails, setClassDetails] = useState(null);
  const [loading, setLoading] = useState(null);
  const [loadingMonths, setLoadingMonths] = useState(true);
  const [classes, setClasses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(-1);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedAvailableMonth, setSelectedAvailableMonth] = useState("");
  const [selectedAvailableMonthDetails, setSelectedAvailableMonthDetails] =
    useState(null);

  const loadDocuments = async () => {
    if (selectedAvailableMonth !== "" && selectedClassId > 0) {
      setDocuments([]);
      setLoading(true);
      try {
        const date = formatDate(selectedAvailableMonth);
        const res = await getScheduleFilesByClassIdAndDateAPI(
          selectedClassId,
          date,
        );
        if (res && res.IsSuccessful && Array.isArray(res.Data)) {
          setDocuments(res.Data);
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error("Error loading schedule documents:", error);
        showErrorToastMsg("Failed to load documents");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const loadClasses = async () => {
    const res = await getClassesAPI();
    if (res && res.IsSuccessful && res.Data && res.Data.length > 0) {
      setClasses(res.Data);
      setSelectedClassId(res.Data[0].ClassId);
    }
  };

  const loadClassSchedules = async () => {
    setLoadingMonths(true);
    try {
      const res = await getPayedInformationAPI(selectedClassId);
      if (res && res.IsSuccessful && res.Data && res.Data.length > 0) {
        setAvailableMonths(res.Data);
        setSelectedAvailableMonth(res.Data[0].FirstDayOfMonth);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
    setLoadingMonths(false);
  };

  useEffect(() => {
    if (selectedAvailableMonth !== "") {
      var data = availableMonths.find(
        (item) => item.FirstDayOfMonth === selectedAvailableMonth,
      );
      setSelectedAvailableMonthDetails(data);
    }

    if (selectedClassId > 0) {
      loadDocuments();
    }
  }, [selectedClassId, selectedAvailableMonth]);

  useEffect(() => {
    if (selectedClassId > 0) {
      var classDetails = classes.find(
        (item) => item.ClassId === selectedClassId,
      );
      setClassDetails(classDetails);
      loadClassSchedules();
    }
  }, [selectedClassId]);

  useEffect(() => {
    loadClasses();
  }, []);

  const handleClassFilterChange = (e) => {
    setSelectedClassId(Number(e.target.value));
  };

  const handleMonthFilterChange = (e) => {
    setSelectedAvailableMonth(e.target.value);
  };

  return (
    <DashboardLayout
      title="Schedule Details"
      subtitle={
        classDetails
          ? `Schedule for ${classDetails.Name}`
          : "View schedule details"
      }
    >
      <div>
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Study Packs</h3>

          <div className="flex flex-wrap items-center gap-4">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Class and Month:
            </label>
            <select
              value={selectedClassId}
              onChange={handleClassFilterChange}
              className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
            >
              <option value="-1">Select Class</option>
              {classes.map((classItem) => (
                <option key={classItem.ClassId} value={classItem.ClassId}>
                  {classItem.Name}
                </option>
              ))}
            </select>

            {loadingMonths ? (
              <Loader text="" />
            ) : (
              <select
                value={selectedAvailableMonth}
                onChange={handleMonthFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
              >
                {availableMonths &&
                  availableMonths.map((m) => {
                    return (
                      <option key={m.FirstDayOfMonth} value={m.FirstDayOfMonth}>
                        {m.MonthYear}
                      </option>
                    );
                  })}
              </select>
            )}
          </div>
        </div>

        {classDetails && <ClassDetailsBox classDetails={classDetails} />}

        {selectedAvailableMonthDetails &&
          selectedAvailableMonthDetails.IsPayed != null &&
          selectedAvailableMonthDetails.IsPayed === false && (
            <PaymentBanner
              price={classDetails.ClassFee}
              classId={classDetails.ClassId}
              date={formatDate(selectedAvailableMonthDetails.FirstDayOfMonth)}
            />
          )}

        {loading && <Loader text="" />}

        {documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex gap-4">
              {documents.map((doc) => {
                return <StudyPackCard document={doc} />;
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudyPacksPage;
